import { useCallback, useEffect, useMemo, useState } from 'react'

import { getMuscleSummary } from '@/api/services/workout'
import type { MuscleSlug, MuscleSummary } from '@/api/types/workout'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useSyncEventListener } from '@/hooks/useSyncEventListener'
import { cn } from '@/lib/utils'

import { MUSCLE_LEVEL_COLORS, muscleLabel, muscleLevel, trainedAgoLabel, withAllMuscles } from '../muscle-level'
import { MuscleBodyMap } from './muscle-body/muscle-body-map'

type Props = {
  /** 渡すとそのセッションだけを表示する（期間の切り替えは出さない） */
  sessionId?: number
}

const PERIODS = [7, 30] as const
type Period = (typeof PERIODS)[number]

const LEGEND = ['1〜2', '3〜5', '6〜9', '10+']

const formatSets = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(1))

/** 鍛えた筋群を人体図に赤く表示するカード */
export const MuscleSummaryCard = ({ sessionId }: Props) => {
  const [days, setDays] = useState<Period>(7)
  const [summary, setSummary] = useState<MuscleSummary | null>(null)
  const [error, setError] = useState(false)

  const load = useCallback(async () => {
    try {
      setSummary(await getMuscleSummary(sessionId !== undefined ? { sessionId } : { days }))
      setError(false)
    } catch (err) {
      console.error(err)
      setError(true)
    }
  }, [days, sessionId])

  useEffect(() => {
    void load()
  }, [load])

  // 種目の筋群やセッションが変わったら描き直す（自分の端末での変更も含む）
  useSyncEventListener(
    useCallback(
      (event) => {
        if (event.type === 'workout.session.changed' || event.type === 'workout.exercise.changed') void load()
      },
      [load],
    ),
  )

  // セッション単体は 1 週間分として濃さを決める
  const levelDays = sessionId !== undefined ? 7 : days

  const colors = useMemo(() => {
    const map = new Map<MuscleSlug, string>()
    for (const m of summary?.muscles ?? []) {
      const color = MUSCLE_LEVEL_COLORS.at(muscleLevel(m.effective_sets, levelDays))
      if (color) map.set(m.muscle, color)
    }
    return map
  }, [summary, levelDays])

  const rows = useMemo(() => {
    if (!summary) return []
    return sessionId !== undefined ? summary.muscles : withAllMuscles(summary.muscles)
  }, [summary, sessionId])

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{sessionId !== undefined ? '鍛えた部位' : '部位ごとのトレーニング'}</h2>
        {sessionId === undefined && (
          <div className="flex gap-1">
            {PERIODS.map((p) => (
              <Button key={p} size="sm" variant={p === days ? 'default' : 'outline'} onClick={() => setDays(p)}>
                {p}日
              </Button>
            ))}
          </div>
        )}
      </div>

      {error ? (
        <div className="text-sm text-muted-foreground">部位の集計を取得できませんでした</div>
      ) : !summary ? (
        <Spinner size={24} />
      ) : (
        <>
          <MuscleBodyMap colors={colors} className="h-64" />

          <div className="flex items-center justify-center gap-2 text-[10px] text-muted-foreground">
            <span>週あたりセット</span>
            {LEGEND.map((label, i) => (
              <span key={label} className="flex items-center gap-0.5">
                <span
                  className="inline-block size-2.5 rounded-sm"
                  style={{ background: MUSCLE_LEVEL_COLORS.at(i + 1) }}
                />
                {label}
              </span>
            ))}
          </div>

          {rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              筋群が設定された種目の記録がありません。種目マスタで筋群を設定してください。
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {rows.map((m) => {
                const ago = trainedAgoLabel(m.last_trained_at)
                return (
                  <div
                    key={m.muscle}
                    className={cn('flex items-center gap-1.5', m.effective_sets === 0 && 'text-muted-foreground')}
                  >
                    <span
                      className="inline-block size-2.5 shrink-0 rounded-sm bg-muted-foreground/25"
                      style={colors.has(m.muscle) ? { background: colors.get(m.muscle) } : undefined}
                    />
                    <span className="flex-1 truncate">{muscleLabel(m.muscle)}</span>
                    <span className="tabular-nums">
                      {m.effective_sets === 0 ? '未実施' : `${formatSets(m.effective_sets)}セット`}
                    </span>
                    {sessionId === undefined && ago && (
                      <span className="w-10 text-right text-muted-foreground">{ago}</span>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </Card>
  )
}
